import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    SignOut,
    Plus,
    Trash,
    Camera,
    FilmSlate,
    PenNib,
    UploadSimple,
    LinkSimple,
    PencilSimple,
    Check,
    X,
    ArrowUp,
    ArrowDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../lib/AuthContext";
import { TYPE_META, resolveSrc } from "../components/Showreel";
import SEO from "../components/SEO";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg";

const SPAN_OPTIONS = [
    { value: "lg:col-span-4", label: "Paysage · petit (1/3)" },
    { value: "lg:col-span-5", label: "Paysage · moyen (5/12)" },
    { value: "lg:col-span-6", label: "Paysage · demi" },
    { value: "lg:col-span-7", label: "Paysage · grand (7/12)" },
    { value: "lg:col-span-12", label: "Paysage · pleine largeur" },
    { value: "lg:col-span-3 lg:row-span-2", label: "Portrait · étroit" },
    { value: "lg:col-span-4 lg:row-span-2", label: "Portrait · standard" },
    { value: "lg:col-span-4 lg:row-span-3", label: "Portrait · haut" },
    { value: "lg:col-span-6 lg:row-span-2", label: "Carré (demi × 2)" },
    { value: "lg:col-span-7 lg:row-span-2", label: "Hero (grand)" },
    { value: "lg:col-span-8 lg:row-span-2", label: "Hero (extra large)" },
];

function formatSpanLabel(spanValue) {
    if (!spanValue) return "";
    const match = SPAN_OPTIONS.find((o) => o.value === spanValue);
    return match ? match.label : spanValue;
}

export default function AdminShowreel() {
    const { user, loading, logout, api, setUser } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        if (loading || !user) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/showreel");
                if (!cancelled) setItems(data);
            } catch (err) {
                if (err?.response?.status === 401) {
                    setUser(null);
                    navigate("/admin");
                    return;
                }
                toast.error("Impossible de charger le showreel.");
            } finally {
                if (!cancelled) setLoadingList(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loading, user, api, navigate, setUser]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-scd text-cream">
                <span className="label-eyebrow text-cream/60">Chargement…</span>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin" replace />;

    const onLogout = async () => {
        await logout();
        navigate("/admin");
    };

    const onCreated = (item) => {
        setItems((prev) => [...prev, item]);
        setShowForm(false);
    };

    const onDelete = async (id) => {
        if (!window.confirm("Supprimer cet élément ?")) return;
        try {
            await api.delete(`/admin/showreel/${id}`);
            setItems((prev) => prev.filter((i) => i.id !== id));
            toast.success("Élément supprimé.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erreur.");
        }
    };

    const onUpdate = async (id, patch) => {
        try {
            const { data } = await api.patch(`/admin/showreel/${id}`, patch);
            setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
            toast.success("Élément mis à jour.");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Impossible d'enregistrer les changements.",
            );
        }
    };

    const onMove = async (id, direction) => {
        // Reorder by swapping positions with the adjacent item (direction: -1 up, +1 down)
        const sorted = [...items].sort(
            (a, b) => (a.position || 0) - (b.position || 0),
        );
        const idx = sorted.findIndex((i) => i.id === id);
        const targetIdx = idx + direction;
        if (idx === -1 || targetIdx < 0 || targetIdx >= sorted.length) return;

        const a = sorted[idx];
        const b = sorted[targetIdx];
        const posA = a.position ?? idx;
        const posB = b.position ?? targetIdx;

        try {
            // Persist both positions
            const [respA, respB] = await Promise.all([
                api.patch(`/admin/showreel/${a.id}`, { position: posB }),
                api.patch(`/admin/showreel/${b.id}`, { position: posA }),
            ]);
            setItems((prev) =>
                prev.map((i) => {
                    if (i.id === respA.data.id) return respA.data;
                    if (i.id === respB.data.id) return respB.data;
                    return i;
                }),
            );
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Impossible de réordonner.",
            );
        }
    };

    return (
        <main
            data-testid="admin-showreel-page"
            className="min-h-screen bg-scd text-cream"
        >
            <SEO title="Réalisations (admin)" path="/admin/showreel" noindex={true} />
            <header className="border-b border-scd bg-[#faf7f1]/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="px-6 md:px-10 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="Studio Cent Douze"
                            className="h-8 w-auto"
                        />
                        <span className="hidden sm:inline label-eyebrow text-yellow">
                            Admin · Réalisations
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/briefs"
                            className="hidden sm:inline label-eyebrow text-cream/70 hover:text-yellow"
                        >
                            Briefs →
                        </Link>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-scd text-cream hover:border-yellow hover:text-yellow text-sm transition-colors"
                        >
                            <SignOut size={16} weight="bold" />
                            Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <section className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl">Nos réalisations</h1>
                        <p className="text-cream/65 text-sm mt-2">
                            Photos, vidéos et logos affichés sur la page d&apos;accueil. Utilise les flèches pour réordonner, le crayon pour modifier le titre / type / format d&apos;affichage.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowForm((s) => !s)}
                        data-testid="admin-showreel-add-button"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow text-[#272727] font-semibold text-sm hover:bg-green hover:text-[#faf7f1] transition-colors"
                    >
                        <Plus size={16} weight="bold" />
                        {showForm ? "Annuler" : "Ajouter un élément"}
                    </button>
                </div>

                {showForm && (
                    <ShowreelForm api={api} onCreated={onCreated} />
                )}

                {loadingList ? (
                    <div className="text-center py-16 text-cream/60 label-eyebrow">
                        Chargement…
                    </div>
                ) : (
                    <div
                        className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                        data-testid="admin-showreel-list"
                    >
                        {[...items]
                            .sort((a, b) => (a.position || 0) - (b.position || 0))
                            .map((it, i, arr) => (
                                <ItemCard
                                    key={it.id}
                                    item={it}
                                    isFirst={i === 0}
                                    isLast={i === arr.length - 1}
                                    onDelete={() => onDelete(it.id)}
                                    onUpdate={(patch) => onUpdate(it.id, patch)}
                                    onMoveUp={() => onMove(it.id, -1)}
                                    onMoveDown={() => onMove(it.id, +1)}
                                />
                            ))}
                    </div>
                )}
            </section>
        </main>
    );
}

function ItemCard({
    item,
    onDelete,
    onUpdate,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
}) {
    const meta = TYPE_META[item.type] || TYPE_META.photo;
    const src = resolveSrc(item.src);
    const poster = resolveSrc(item.poster);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        title: item.title || "",
        type: item.type,
        span: item.span || "lg:col-span-4",
    });

    const cancelEdit = () => {
        setDraft({
            title: item.title || "",
            type: item.type,
            span: item.span || "lg:col-span-4",
        });
        setEditing(false);
    };

    const saveEdit = async () => {
        await onUpdate({
            title: draft.title.trim(),
            type: draft.type,
            span: draft.span,
        });
        setEditing(false);
    };

    return (
        <div
            className="border border-scd bg-elev rounded-sm overflow-hidden"
            data-testid={`admin-showreel-card-${item.id}`}
        >
            <div className="relative aspect-[4/3] bg-scd">
                {item.type === "video" ? (
                    <video
                        src={src}
                        poster={poster}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <img
                        src={src}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                )}
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-[#272727]/85 backdrop-blur">
                    <meta.Icon size={11} weight="bold" className="text-yellow" />
                    <span className="text-[10px] tracking-wider uppercase text-cream/85">
                        {meta.label}
                    </span>
                </div>

                {/* Reorder controls */}
                <div className="absolute top-2 right-2 flex flex-col gap-1">
                    <button
                        type="button"
                        onClick={onMoveUp}
                        disabled={isFirst}
                        data-testid={`admin-showreel-up-${item.id}`}
                        className="w-7 h-7 rounded-sm bg-[#272727]/85 backdrop-blur text-cream disabled:opacity-30 disabled:cursor-not-allowed hover:bg-yellow hover:text-[#272727] transition-colors grid place-items-center"
                        aria-label="Monter"
                        title="Monter dans l'ordre"
                    >
                        <ArrowUp size={12} weight="bold" />
                    </button>
                    <button
                        type="button"
                        onClick={onMoveDown}
                        disabled={isLast}
                        data-testid={`admin-showreel-down-${item.id}`}
                        className="w-7 h-7 rounded-sm bg-[#272727]/85 backdrop-blur text-cream disabled:opacity-30 disabled:cursor-not-allowed hover:bg-yellow hover:text-[#272727] transition-colors grid place-items-center"
                        aria-label="Descendre"
                        title="Descendre dans l'ordre"
                    >
                        <ArrowDown size={12} weight="bold" />
                    </button>
                </div>
            </div>

            <div className="p-3 space-y-2">
                {editing ? (
                    <>
                        <input
                            data-testid={`admin-showreel-edit-title-${item.id}`}
                            value={draft.title}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, title: e.target.value }))
                            }
                            placeholder="Titre"
                            className="w-full bg-scd border border-scd rounded-sm px-2 py-1.5 text-cream text-sm focus:border-yellow outline-none"
                        />
                        <select
                            data-testid={`admin-showreel-edit-type-${item.id}`}
                            value={draft.type}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, type: e.target.value }))
                            }
                            className="w-full bg-scd border border-scd rounded-sm px-2 py-1.5 text-cream text-xs focus:border-yellow outline-none"
                        >
                            <option value="photo">Photo</option>
                            <option value="video">Vidéo</option>
                            <option value="logo">Logo / Branding</option>
                        </select>
                        <select
                            data-testid={`admin-showreel-edit-span-${item.id}`}
                            value={draft.span}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, span: e.target.value }))
                            }
                            className="w-full bg-scd border border-scd rounded-sm px-2 py-1.5 text-cream text-xs focus:border-yellow outline-none"
                        >
                            {SPAN_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={saveEdit}
                                data-testid={`admin-showreel-save-${item.id}`}
                                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm bg-yellow text-[#272727] text-xs font-semibold hover:bg-green hover:text-[#faf7f1] transition-colors"
                            >
                                <Check size={12} weight="bold" />
                                Enregistrer
                            </button>
                            <button
                                onClick={cancelEdit}
                                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-sm border border-scd text-cream/70 text-xs hover:border-cream/50"
                            >
                                <X size={12} weight="bold" />
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-sm text-cream/90 truncate" title={item.title}>
                            {item.title || "Sans titre"}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                            <span
                                className="text-[10px] text-cream/45 truncate flex-1"
                                title={item.span}
                            >
                                {formatSpanLabel(item.span) || "auto"}
                            </span>
                            <button
                                onClick={() => setEditing(true)}
                                data-testid={`admin-showreel-edit-${item.id}`}
                                className="p-1.5 rounded-sm text-cream/70 hover:text-yellow hover:bg-yellow/10"
                                aria-label="Modifier"
                                title="Modifier"
                            >
                                <PencilSimple size={14} />
                            </button>
                            <button
                                onClick={onDelete}
                                data-testid={`admin-showreel-delete-${item.id}`}
                                className="p-1.5 rounded-sm text-[#ff6b6b] hover:bg-[#ff6b6b]/10"
                                aria-label="Supprimer"
                                title="Supprimer"
                            >
                                <Trash size={14} />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function ShowreelForm({ api, onCreated }) {
    const [type, setType] = useState("photo");
    const [title, setTitle] = useState("");
    const [span, setSpan] = useState("lg:col-span-4");
    const [mode, setMode] = useState("file"); // file | url
    const [file, setFile] = useState(null);
    const [fileKey, setFileKey] = useState(0);
    const [url, setUrl] = useState("");
    const [poster, setPoster] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (mode === "file" && !file) {
            toast.error("Choisissez un fichier ou utilisez une URL.");
            return;
        }
        if (mode === "url" && !url.trim()) {
            toast.error("Entrez une URL.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("type", type);
            fd.append("title", title);
            fd.append("span", span);
            if (poster) fd.append("poster", poster);
            if (mode === "file" && file) {
                fd.append("file", file);
            } else if (mode === "url") {
                fd.append("url", url.trim());
            }
            const { data } = await api.post("/admin/showreel", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onCreated(data);
            toast.success("Élément ajouté.");
            // reset
            setTitle("");
            setFile(null);
            setFileKey((k) => k + 1);
            setUrl("");
            setPoster("");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Erreur lors de l'ajout.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-scd bg-elev rounded-sm p-6 space-y-5 mb-4"
            data-testid="admin-showreel-form"
        >
            <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Type">
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="scd-input"
                        data-testid="admin-showreel-type"
                    >
                        <option value="photo">Photo</option>
                        <option value="video">Vidéo</option>
                        <option value="logo">Logo</option>
                    </select>
                </Field>
                <Field label="Titre">
                    <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="scd-input"
                        placeholder="Photo · Lifestyle"
                        data-testid="admin-showreel-title"
                    />
                </Field>
                <Field label="Taille (bento)">
                    <select
                        value={span}
                        onChange={(e) => setSpan(e.target.value)}
                        className="scd-input"
                        data-testid="admin-showreel-span"
                    >
                        {SPAN_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </Field>
            </div>

            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setMode("file")}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm border text-sm ${
                        mode === "file"
                            ? "bg-yellow text-[#272727] border-yellow"
                            : "border-scd text-cream/85 hover:border-yellow"
                    }`}
                >
                    <UploadSimple size={14} weight="bold" /> Fichier
                </button>
                <button
                    type="button"
                    onClick={() => setMode("url")}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm border text-sm ${
                        mode === "url"
                            ? "bg-yellow text-[#272727] border-yellow"
                            : "border-scd text-cream/85 hover:border-yellow"
                    }`}
                >
                    <LinkSimple size={14} weight="bold" /> URL externe
                </button>
            </div>

            {mode === "file" ? (
                <Field
                    label={`Fichier ${
                        type === "video"
                            ? "(MP4, WEBM, MOV — max 25 MB)"
                            : "(JPG, PNG, WEBP, SVG — max 25 MB)"
                    }`}
                >
                    <input
                        key={fileKey}
                        type="file"
                        accept={type === "video" ? "video/*" : "image/*"}
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="scd-input"
                        data-testid="admin-showreel-file"
                    />
                </Field>
            ) : (
                <Field label="URL de l'asset">
                    <input
                        type="text"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        className="scd-input"
                        placeholder="https://..."
                        data-testid="admin-showreel-url"
                    />
                </Field>
            )}

            {type === "video" && (
                <Field label="URL miniature (poster) — optionnel">
                    <input
                        type="text"
                        value={poster}
                        onChange={(e) => setPoster(e.target.value)}
                        className="scd-input"
                        placeholder="https://... .jpg"
                        data-testid="admin-showreel-poster"
                    />
                </Field>
            )}

            <div className="flex justify-end">
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="admin-showreel-submit"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow text-[#272727] font-semibold text-sm hover:bg-green hover:text-[#faf7f1] transition-colors disabled:opacity-60"
                >
                    {submitting ? "Ajout…" : "Ajouter"}
                </button>
            </div>

            <style>{`
                .scd-input {
                    width: 100%;
                    background: #faf7f1;
                    color: #272727;
                    border: 1px solid rgba(39,39,39,0.18);
                    padding: 0.7rem 0.9rem;
                    border-radius: 4px;
                    font-family: "Manrope", sans-serif;
                    font-size: 0.9rem;
                }
                .scd-input:focus { outline: none; border-color: #3b5838; }
                select.scd-input { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23272727' stroke-width='1.5' d='M1 1l5 5 5-5'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 1rem center; padding-right: 2.5rem; }
            `}</style>
        </motion.form>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow text-cream/70 mb-2 block">{label}</span>
            {children}
        </label>
    );
}
